import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Typography,
  useTheme
} from "@mui/material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import svLocale from "date-fns/locale/sv"
import { Form, Formik, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import React, { useEffect, useRef, useState } from "react"
import {
  StyledButtonContainer,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  StyledStepContent,
} from "./CreateTemplateModal.styled"
import {
  CreateTemplateModalProps,
  Vehicle,
  VehicleGroup,
  TemplateFormData,
  IoTOdometerStatus
} from "./CreateTemplateModal.interfaces"
import { getValidationSchemas } from "./CreateTemplateModal.validation"
import { CheckTemplateIntervalType, CheckTemplateSourceType } from "../../types/CheckTypes.interfaces"
import { BasicSettingsStep } from "./steps/BasicSettingsStep"
import { VehicleSelectionStep } from "./steps/VehicleSelectionStep"
import { LastCheckValuesStep } from "./steps/LastCheckValuesStep"
import SummaryStep from "./steps/SummaryStep"
import DefaultButton from "components/Button"
import { useTranslation } from "react-i18next"
import { StyledStepLabel, StyledStepper } from "shared/styled/General"
import { IStepIconProps } from "components/Form/Stepper/formstepper.interface"

const hasDataChanged = (
  currentValues: TemplateFormData,
  initialFormValues: TemplateFormData,
  checkTemplate: any
): boolean => {
  if (!checkTemplate) return false

  const initial = initialFormValues

  if (currentValues.name !== initial.name) return true
  if (currentValues.usesDate !== initial.usesDate) return true
  if (currentValues.usesDistance !== initial.usesDistance) return true
  if (currentValues.usesInspection !== initial.usesInspection) return true
  if (currentValues.dateInterval !== initial.dateInterval) return true
  if (currentValues.distanceInterval !== initial.distanceInterval) return true
  if (currentValues.dateCalculationType !== initial.dateCalculationType) return true
  if (currentValues.distanceCalculationType !== initial.distanceCalculationType) return true

  const initialVehicles = [...initial.selectedVehicles].sort((a, b) => a - b)
  const currentVehicles = [...currentValues.selectedVehicles].sort((a, b) => a - b)
  if (JSON.stringify(initialVehicles) !== JSON.stringify(currentVehicles)) return true

  for (const vehicleId of currentValues.selectedVehicles) {
    const initialValue = initial.vehicleLastValues[vehicleId]
    const currentValue = currentValues.vehicleLastValues[vehicleId]

    if (!initialValue && currentValue) return true
    if (initialValue && !currentValue) return true

    if (initialValue && currentValue) {
      const initialDate = initialValue.lastDate ? new Date(initialValue.lastDate).getTime() : null
      const currentDate = currentValue.lastDate ? new Date(currentValue.lastDate).getTime() : null
      if (initialDate !== currentDate) return true

      if (initialValue.lastDistance !== currentValue.lastDistance) return true
    }
  }

  return false
}

const getSteps = (t: any) => [
  t("global.basic_settings", "Basic settings"),
  t("global.select_vehicle", "Select vehicles"),
  t("checks.input_previous_values", "Input previous service values"),
  t("global.summary", "Summary")
]

const initialFormData: TemplateFormData = {
  id: undefined,
  name: "",
  usesDate: false,
  usesDistance: false,
  usesInspection: false,
  dateInterval: 0,
  distanceInterval: 0,
  dateCalculationType: "Dynamic",
  distanceCalculationType: "Dynamic",
  selectedVehicles: [],
  vehicleLastValues: {}
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  open,
  onClose,
  onComplete,
  checkTemplate,
  checkTemplateResult,
  checkTemplateListResult
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [showInternalNumber, setShowInternalNumber] = useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [vehiclesResult, vehiclesApi] = useApi() as [IApiResult, IApiFunc]
  const [originalServiceValues, setOriginalServiceValues] = useState<any>({})
  const [sessionServiceValues, setSessionServiceValues] = useState<any>({})
  const [initialFormValues, setInitialFormValues] = useState<TemplateFormData>(initialFormData)
  const formikRef = useRef<any>(null)
  const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([])
  const [vehicleGroupsResult, vehicleGroupsApi] = useApi() as [IApiResult, IApiFunc]
  const [vehiclesDetailsResult, vehiclesDetailsApi] = useApi() as [IApiResult, IApiFunc]
  const [vehicleInspectionData, setVehicleInspectionData] = useState<{ [vehicleId: number]: any }>({})
  const [iotOdometerResult, iotOdometerApi] = useApi() as [IApiResult, IApiFunc]
  const [vehicleIoTOdometerData, setVehicleIoTOdometerData] = useState<{
    [vehicleId: number]: IoTOdometerStatus
  }>({})
  const { t } = useTranslation()
  const steps = getSteps(t)
  const theme = useTheme()

  useEffect(() => {
    if (!open) return
    vehicleGroupsApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
  }, [open, vehicleGroupsApi])

  useEffect(() => {
    if (vehicleGroupsResult.status !== 2) return
    const mappedGroups = vehicleGroupsResult.data.map((group: any) => ({
      id: group.id,
      name: group.name,
      vehicleIds: group.vehiclesInfo ? group.vehiclesInfo.map((v: any) => v.id) : [],
      vehicleCount: group.vehiclesInfo ? group.vehiclesInfo.length : 0
    }))
    setVehicleGroups(mappedGroups)
  }, [vehicleGroupsResult])

  useEffect(() => {
    if (vehiclesDetailsResult.status !== 2) return

    const updatedInspectionData: { [vehicleId: number]: any } = {}
    for (const [vehicleId, vehicle] of Object.entries(vehicleInspectionData)) {
      const vehicleDetails = vehiclesDetailsResult?.data?.find(
        (v: any) => v?.registreringsnummer === vehicle?.regNumber
      )

      const lastInspectionDate = new Date(vehicleDetails?.besiktningsdatum)
      const inspectionDeadline = new Date(vehicleDetails?.besiktningsdatumtom)
      const meterReading = 1337

      updatedInspectionData[parseInt(vehicleId)] = {
        ...vehicle,
        lastInspectionDate,
        inspectionDeadline,
        meterReading
      }
    }

    setVehicleInspectionData(updatedInspectionData)
  }, [vehiclesDetailsResult])

  const fetchIoTOdometerDataForVehicles = (vehicleIds: number[]) => {
    if (vehicleIds.length <= 0) return
    const url = apiEndpoints.vehicleregistry.getiotodometerstatusbatch
    iotOdometerApi.post(url, vehicleIds)
  }

  useEffect(() => {
    if (iotOdometerResult.status !== 2) return
    setVehicleIoTOdometerData(iotOdometerResult.data)
  }, [iotOdometerResult])

  // Function to determine group selection state
  const getGroupSelectionState = (group: VehicleGroup, selectedVehicles: number[]) => {
    const vehiclesInGroup = group.vehicleIds.filter((id) => vehicles.some((v) => v.id === id))
    const selectedInGroup = vehiclesInGroup.filter((id) => selectedVehicles.includes(id))

    if (selectedInGroup.length === 0) return "none"
    if (selectedInGroup.length === vehiclesInGroup.length) return "all"
    return "some"
  }

  const toggleGroupSelection = async (group: VehicleGroup, formik: any) => {
    const vehiclesInGroup = group.vehicleIds.filter((id) => vehicles.some((v) => v.id === id))
    const currentState = getGroupSelectionState(group, formik.values.selectedVehicles)

    if (currentState === "all") {
      // Deselect all vehicles in group
      const newSelection = formik.values.selectedVehicles.filter(
        (id: number) => !vehiclesInGroup.includes(id)
      )
      await formik.setFieldValue("selectedVehicles", newSelection, true)
      await formik.setFieldTouched("selectedVehicles", true, false)

      // Preserve values for deselected vehicles
      vehiclesInGroup.forEach((vehicleId) => {
        if (formik.values.vehicleLastValues[vehicleId]) {
          setSessionServiceValues((prev: any) => ({
            ...prev,
            [vehicleId]: formik.values.vehicleLastValues[vehicleId]
          }))
        }
      })
    } else {
      // Select all vehicles in group
      const newSelection = Array.from(new Set([...formik.values.selectedVehicles, ...vehiclesInGroup]))
      await formik.setFieldValue("selectedVehicles", newSelection, true)
      await formik.setFieldTouched("selectedVehicles", true, false)

      // Restore values for newly selected vehicles
      vehiclesInGroup.forEach((vehicleId) => {
        if (!formik.values.selectedVehicles.includes(vehicleId)) {
          const currentValues = formik.values.vehicleLastValues[vehicleId]
          const sessionValues = sessionServiceValues[vehicleId]
          const originalValues = originalServiceValues[vehicleId]
          const restoredValues = currentValues || sessionValues || originalValues || {}
          formik.setFieldValue(`vehicleLastValues.${vehicleId}`, restoredValues)
        }
      })
    }
  }

  useEffect(() => {
    if (open) {
      vehiclesApi.reset()
      vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)

      // Reset form or load template data
      if (checkTemplate) {
        // Editing existing template - map API response to form structure
        let usesDate = false
        let usesDistance = false
        let usesInspection = false
        let dateInterval = 365
        let distanceInterval = 5000
        let dateCalculationType: "Dynamic" | "Static" = "Dynamic"
        let distanceCalculationType: "Dynamic" | "Static" = "Dynamic"

        // Parse intervals array if it exists
        if (checkTemplate.intervals && Array.isArray(checkTemplate.intervals)) {
          checkTemplate.intervals.forEach((interval: any) => {
            if (
              interval.type === CheckTemplateIntervalType.Days &&
              interval.sourceType === CheckTemplateSourceType.TransportStyrelsen
            ) {
              // TransportStyrelsen inspection interval
              usesInspection = true
            } else if (interval.type === CheckTemplateIntervalType.Days) {
              usesDate = true
              dateInterval = interval.value || 365
              dateCalculationType = interval.calculationType === 2 ? "Static" : "Dynamic"
            } else if (interval.type === CheckTemplateIntervalType.Distance) {
              usesDistance = true
              distanceInterval = interval.value || 5000
              distanceCalculationType = interval.calculationType === 2 ? "Static" : "Dynamic"
            }
          })
        }

        // Extract vehicle IDs from vehicles array or use vehicleIds directly
        let selectedVehicles: number[] = []
        if (checkTemplate.vehicles && Array.isArray(checkTemplate.vehicles)) {
          selectedVehicles = checkTemplate.vehicles.map((v: any) => v.vehicleId || v.id)
        } else if (checkTemplate.vehicleIds && Array.isArray(checkTemplate.vehicleIds)) {
          selectedVehicles = checkTemplate.vehicleIds
        }

        // Extract vehicle last values from the vehicles array and their services
        let vehicleLastValues: any = {}

        if (checkTemplate.vehicles && Array.isArray(checkTemplate.vehicles)) {
          checkTemplate.vehicles.forEach((vehicle: any) => {
            if (vehicle.services && Array.isArray(vehicle.services)) {
              const vehicleId = vehicle.vehicleId || vehicle.id
              const dateService = vehicle.services.find((s: any) => s.intervalType === "Days")
              const distanceService = vehicle.services.find((s: any) => s.intervalType === "Distance")

              // Handle date service - check both lastCompletedDate and lastCompletedValue
              let lastDate = null
              if (dateService) {
                if (dateService.lastCompletedDate) {
                  lastDate = new Date(dateService.lastCompletedDate)
                } else if (dateService.lastCompletedValue) {
                  // Try to parse as date
                  const parsedDate = new Date(dateService.lastCompletedValue)
                  if (!isNaN(parsedDate.getTime())) {
                    lastDate = parsedDate
                  }
                }
              }

              // Handle distance service - check both lastCompletedDistance and lastCompletedValue
              let lastDistance = undefined
              if (distanceService) {
                if (
                  distanceService.lastCompletedDistance !== null &&
                  distanceService.lastCompletedDistance !== undefined
                ) {
                  lastDistance = Number(distanceService.lastCompletedDistance)
                } else if (
                  distanceService.lastCompletedValue !== null &&
                  distanceService.lastCompletedValue !== undefined &&
                  distanceService.lastCompletedValue !== ""
                ) {
                  // Try to parse as number
                  const parsedDistance = Number(distanceService.lastCompletedValue)
                  if (!isNaN(parsedDistance)) {
                    lastDistance = parsedDistance
                  }
                }
              }

              vehicleLastValues[vehicleId] = {
                lastDate: lastDate,
                lastDistance: lastDistance
              }
            }
          })
        } else if (checkTemplate.vehicleServices && Array.isArray(checkTemplate.vehicleServices)) {
          checkTemplate.vehicleServices.forEach((service: any) => {
            vehicleLastValues[service.vehicleId] = {
              lastDate: service.lastCompletedDate ? new Date(service.lastCompletedDate) : null,
              lastDistance: service.lastCompletedDistance || undefined
            }
          })
        } else if (checkTemplate.vehicleLastValues) {
          vehicleLastValues = checkTemplate.vehicleLastValues
        }
        // Store the original values for preservation
        setOriginalServiceValues(vehicleLastValues)
        setSessionServiceValues(vehicleLastValues)

        setInitialFormValues({
          id: checkTemplate.id,
          name: checkTemplate.name || "",
          usesDate: usesDate,
          usesDistance: usesDistance,
          usesInspection: usesInspection,
          dateInterval: dateInterval,
          distanceInterval: distanceInterval,
          dateCalculationType: dateCalculationType,
          distanceCalculationType: distanceCalculationType,
          selectedVehicles: selectedVehicles,
          vehicleLastValues: vehicleLastValues
        })
      } else {
        // Creating new template
        setOriginalServiceValues({})
        setSessionServiceValues({})
        setInitialFormValues(initialFormData)
      }
      setActiveStep(0) // Reset to first step
      setFormKey((prev) => prev + 1) // Force form reset
      setVehicleInspectionData({}) // Clear inspection data
    }

    return () => {
      vehiclesApi.leave()
    }
  }, [open, checkTemplate, vehiclesApi])

  useEffect(() => {
    if (vehiclesResult.status !== 2) return
    setVehicles(vehiclesResult.data)
  }, [vehiclesResult])

  // Use initialFormValues from state instead of hardcoded values

  const handleNext = (formikValues?: TemplateFormData) => {
    setActiveStep((prevStep) => prevStep + 1)

    const nextStep = activeStep + 1
    if (nextStep === 2 && formikValues?.usesInspection && formikValues.selectedVehicles.length > 0) {
      fetchInspectionDataForVehicles(formikValues.selectedVehicles, true)
    }

    if (nextStep === 2 && formikValues?.usesDistance && formikValues.selectedVehicles.length > 0) {
      fetchIoTOdometerDataForVehicles(formikValues.selectedVehicles)
    }
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleCloseAttempt = () => {
    if (checkTemplate && formikRef.current && !hasDataChanged(formikRef.current.values, initialFormValues, checkTemplate)) {
      handleConfirmClose()
      return
    }
    setConfirmCloseOpen(true)
  }

  const handleCancelClose = () => {
    setConfirmCloseOpen(false)
  }

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false)
    setActiveStep(0)
    setVehicles([])
    setShowInternalNumber(false)
    setFormKey((prev) => prev + 1)
    setOriginalServiceValues({})
    setSessionServiceValues({})
    setVehicleInspectionData({})
    setVehicleIoTOdometerData({})
    onClose()
  }

  const fetchInspectionDataForVehicles = (vehicleIds: number[], usesInspection: boolean) => {
    const inspectionData: { [vehicleId: number]: any } = {}
    const selectedVehicles = vehiclesResult?.data
      ?.filter((vehicle: any) => vehicleIds.includes(vehicle?.id))
      .map((vehicle: any) => {
        inspectionData[vehicle?.id] = {
          ...vehicle,
          regNumber: vehicle?.regNumber
        }
        return vehicle?.regNumber?.toString()
      })
    setVehicleInspectionData(inspectionData)
    vehiclesDetailsApi.post(apiEndpoints.vehicleregistry.getvehicledetailsbyregnrlist, selectedVehicles)
  }

  const StepIcon: React.FC<IStepIconProps> = (props) => {
    const { active, completed } = props
    return active ? null : completed ? null : null
  }


  const RenderStepContent = ({ step }: { step: number }) => {
    switch (step) {
      case 0:
        return <BasicSettingsStep setSessionServiceValues={setSessionServiceValues} />

      case 1:
        return (
          <VehicleSelectionStep
            vehicles={vehicles}
            vehiclesResult={vehiclesResult}
            vehicleGroups={vehicleGroups}
            showInternalNumber={showInternalNumber}
            setShowInternalNumber={setShowInternalNumber}
            sessionServiceValues={sessionServiceValues}
            setSessionServiceValues={setSessionServiceValues}
            originalServiceValues={originalServiceValues}
            getGroupSelectionState={getGroupSelectionState}
            toggleGroupSelection={toggleGroupSelection}
            checkTemplateListResult={checkTemplateListResult}
            checkTemplate={checkTemplate}
          />
        )

      case 2:
        return (
          <LastCheckValuesStep
            vehicles={vehicles}
            showInternalNumber={showInternalNumber}
            setShowInternalNumber={setShowInternalNumber}
            sessionServiceValues={sessionServiceValues}
            setSessionServiceValues={setSessionServiceValues}
            originalServiceValues={originalServiceValues}
            vehicleInspectionData={vehicleInspectionData}
            vehicleIoTOdometerData={vehicleIoTOdometerData}
            fetchInspectionDataForVehicles={fetchInspectionDataForVehicles}
          />
        )

      case 3:
        return (
          <SummaryStep
            setShowInternalNumber={setShowInternalNumber}
            vehicles={vehicles}
            showInternalNumber={showInternalNumber}
            sessionServiceValues={sessionServiceValues}
            originalServiceValues={originalServiceValues}
            vehicleInspectionData={vehicleInspectionData}
          />
        )

      default:
        return null
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={svLocale}>
      <StyledDialog
        open={open}
        maxWidth={false}
        fullWidth
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason && reason === "backdropClick") {
            return
          }
          handleCloseAttempt()
        }}
      >
        <StyledDialogTitle>
          <Typography variant="h3">
            {checkTemplate ? `${t("global.edit", "Edit")} + ${checkTemplate.name || t("global.check_template", "Check template")}` : t("checks.create_template", "Create check template")}
          </Typography>
          <IconButton sx={{ position: "absolute", right: theme.spacing(3), top: theme.spacing(3) }} onClick={handleCloseAttempt} size="small">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          <StyledStepper connector={null} sx={{ gap: 2, flexWrap: "wrap" }} activeStep={activeStep} orientation="horizontal">
            {steps.map((label, index) => (
              <Step sx={{ padding: 0 }} key={label}>
                <StyledStepLabel StepIconComponent={StepIcon}>
                  {`${index + 1}. ${label}`}
                </StyledStepLabel>
              </Step>

            ))}
          </StyledStepper>

          <Formik
            key={formKey}
            initialValues={initialFormValues}
            validationSchema={getValidationSchemas(checkTemplateListResult, t, vehicleIoTOdometerData)[activeStep]}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values) => {
              if (activeStep === steps.length - 1) {
                onComplete(values)
              } else {
                handleNext(values)
              }
            }}>
            {(formik) => {
              formikRef.current = formik

              return (
                <Form style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                  <StyledStepContent>
                    <RenderStepContent step={activeStep} />
                  </StyledStepContent>
                  <StyledButtonContainer>
                    {activeStep === 0 ? <Button
                      onClick={handleCloseAttempt}
                    >
                      {t("global.cancel", "Cancel")}
                    </Button>
                      : <Button disabled={activeStep === 0} onClick={handleBack}>
                        {t("global.back", "Back")}
                      </Button>}

                    <Box>
                      <DefaultButton
                        result={checkTemplateResult}
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          // Manually validate and mark fields as touched
                          const errors = await formik.validateForm()

                          // Mark all fields as touched to show errors
                          if (activeStep === 2 && formik.values.selectedVehicles.length > 0) {
                            const touchedFields: any = { vehicleLastValues: {} }
                            formik.values.selectedVehicles.forEach((vehicleId: number) => {
                              touchedFields.vehicleLastValues[vehicleId] = {
                                lastDate: true,
                                lastDistance: true
                              }
                            })
                            formik.setTouched(touchedFields, true)
                          } else {
                            const touched: any = {
                              name: true,
                              selectedVehicles: true,
                              usesDate: true,
                              usesDistance: true,
                              usesInspection: true
                            }
                            if (formik.values.usesDate) {
                              touched.dateInterval = true
                            }
                            if (formik.values.usesDistance) {
                              touched.distanceInterval = true
                            }
                            formik.setTouched(touched)
                          }

                          // If no errors, submit the form
                          if (Object.keys(errors).length === 0) {
                            formik.handleSubmit()
                          }
                        }}
                        disabled={activeStep === 1 && vehiclesResult.status === 1}>
                        {activeStep === steps.length - 1
                          ? checkTemplate
                            ? t("global.update_template", "Update template")
                            : t("global.create_template", "Create template")
                          : t("global.buttons.next", "Next")}
                      </DefaultButton>
                    </Box>
                  </StyledButtonContainer>
                </Form>
              )
            }}
          </Formik>
        </StyledDialogContent>

        <Dialog open={confirmCloseOpen} onClose={handleCancelClose} maxWidth="sm" fullWidth>
          <Typography variant="h4" p={3} pb={2}>
            {t("global.are_you_sure_cancel", "Are you sure you want to cancel?")}
          </Typography>
          <Box p={3} pt={0}>
            <Typography>
              {!checkTemplate
                ? t("global.are_you_sure_cancel_desc", "You have started a new basis that has not yet been saved. If you close now, all information will be lost. Do you want to continue?")
                : t("global.edit_are_you_sure_cancel_desc", "You have unsaved changes. If you close now, all changes will be lost. Do you want to continue?")
              }
            </Typography>
            <Box mt={3} sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={handleCancelClose}>{t("global.cancel", "Cancel")}</Button>
              <Button onClick={handleConfirmClose} variant="contained">
                {t("global.yes_leave_anyway", "Yes, leave anyway")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </StyledDialog>
    </LocalizationProvider>
  )
}
